import { Controller, useForm } from "react-hook-form";
import React, { useEffect, useRef, useState } from "react";

import { ReactComponent as BackIcon } from "../../components/icons/arrow_icon.svg";
import DatePicker from "react-datepicker";
import Error from "../common/ServerError";
import { NavLink } from "react-router-dom";
import OverFlowToolTip from "../common/OverFlowToolTip";
import Spinner from "../common/Spinner";
import dateHelper from "../common/dateHelper";
import { isDateInThePast } from "../common/utilities";
import { isTrue } from "../common/utilities";
import projectAttributeApi from "../../api/projectAttributeApi";
import projectCategoryApi from "../../api/projectCategoryApi";
import { tokenRequest } from "../../authConfig";
import unitOfMeasurements from "../../constants/unitOfMeasurements";
import uomConfigApi from "../../api/uomConfigApi";
import uomData from "../../constants/uoMData";
import { useMsal } from "@azure/msal-react";
import workflowStatusApi from "../../api/workflowStatusApi";

const EditProject = (props) => {
  const { instance, accounts } = useMsal();
  const [error, setError] = useState(false);
  const [projectCategories, setProjectCategories] = useState([]);
  const [projectAttributes, setProjectAttributes] = useState(null);
  const [workflowStatuses, setWorkflowStatuses] = useState([]);

  const [dataLoaded, setDataLoaded] = useState({
    categoriesLoaded: false,
    currencyLoaded: false,
    attributesLoaded: false,
    workflowStatusesLoaded: false,
  });

  const [currencyData, setCurrencyData] = useState([]);
  const [showCurrency, setShowCurrency] = useState(
    props.currentProject.currency != null ? true : false
  );

  const [getYearAndMonthRange, datePlaceholder] = dateHelper();

  const [project, setProject] = useState({
    ...props.currentProject,
  });

  const [showTimeHorizonValuesError, setTimeHorizonValuesError] =
    useState(false);

  const [timeHorizonValues, setTimeHorizonValues] = useState([
    ...project.projectTimeHorizon,
  ]);

  const originalTimeHorizonValues = [...project.projectTimeHorizon];

  const fromDate = new Date(props.currentProject.validFrom);
  const toDate = new Date(props.currentProject.validTo);

  const [datesTouched, setDatesTouched] = useState(false);
  const [dates, setDates] = useState({
    validFrom: fromDate,
    validTo: toDate,
    refreshTimeHorizonTable: false,
  });

  const todayDate = new Date();
  const [validFromDate, setValidFromDate] = useState(fromDate);
  const [validToDate, setValidToDate] = useState(toDate);
  const validToMaxDate = new Date(
    todayDate.getFullYear(),
    todayDate.getMonth() + (props.maxTimeHorizonInMonths - 1),
    1
  );

  const dateFormat = datePlaceholder();

  const attributeDefaultValuesObject =
    props.currentProject.projectAttributeDetails?.reduce(function (
      result,
      detail
    ) {
      result[detail.attribute.name] = detail.attributeId;
      return result;
    },
    {});

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    reset,
    watch,
  } = useForm({
    defaultValues: {
      ...attributeDefaultValuesObject,
      id: props.currentProject.id,
      name: props.currentProject.name,
      workflowStatus: props.currentProject.workflowStatus,
      projectCategoryId: props.currentProject.projectCategoryId,
      uoM: props.currentProject.uoM,
      currency: props.currentProject.currency,
      validFrom: fromDate,
      validTo: toDate,
      description: props.currentProject.description,
      timeHorizonColumnName:
        props.currentProject?.projectFileUpload?.timeHorizonColumnName,
    },
  });

  const {
    register: timeHorizonRegister,
    formState: { errors: timeHorizonErrors },
    handleSubmit: timeHorizonHandleSubmit,
  } = useForm();

  const fileInputRef = useRef(null);
  const { ref, ...rest } = register("file");

  const [file, setFile] = useState();
  const saveFile = (e) => {
    const file = e.target.files[0];
    setFile(file);
  };

  const [fileUploadingStatus, setFileUploadingStatus] = useState({
    isUploading: false,
    response: null,
  });

  useEffect(() => {
    if (fileUploadingStatus && fileUploadingStatus.response) {
      fileInputRef.current.value = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUploadingStatus]);

  useEffect(() => {
    if (!instance || !accounts || accounts.length === 0) {
      return;
    }

    const accessTokenRequest = {
      ...tokenRequest,
      account: accounts[0],
    };

    instance
      .acquireTokenSilent(accessTokenRequest)
      .then((accessTokenResponse) => {
        const accessToken = accessTokenResponse.accessToken;
        (async () => {
          const data = await projectCategoryApi
            .list(accessToken)
            .then((data) => {
              setError(null);
              return data;
            })
            .catch((error) => {
              setError(error);
              console.log(error);
            })
            .finally(() =>
              setDataLoaded((prevData) => {
                return {
                  ...prevData,
                  categoriesLoaded: true,
                };
              })
            );

          setProjectCategories(data);
        })();
      })
      .catch((error) => {
        setError(error);
      });
  }, [instance, accounts]);

  useEffect(() => {
    if (!instance || !accounts || accounts.length === 0) {
      return;
    }

    const accessTokenRequest = {
      ...tokenRequest,
      account: accounts[0],
    };

    instance
      .acquireTokenSilent(accessTokenRequest)
      .then((accessTokenResponse) => {
        const accessToken = accessTokenResponse.accessToken;
        (async () => {
          const data = await uomConfigApi
            .list(accessToken)
            .then((data) => {
              setError(false);
              return data;
            })
            .catch((e) => {
              setError(true);
              console.log(e);
            })
            .finally(() =>
              setDataLoaded((prevData) => {
                return {
                  ...prevData,
                  currencyLoaded: true,
                };
              })
            );

          setCurrencyData(data.currency);
        })();
      })
      .catch((error) => {
        setError(true);
      });
  }, [instance, accounts]);

  useEffect(() => {
    if (!isTrue(props.configuration.enableProjectAttributes)) {
      return;
    }

    if (!instance || !accounts || accounts.length === 0) {
      return;
    }

    const accessTokenRequest = {
      ...tokenRequest,
      account: accounts[0],
    };

    instance
      .acquireTokenSilent(accessTokenRequest)
      .then((accessTokenResponse) => {
        const accessToken = accessTokenResponse.accessToken;
        (async () => {
          const data = await projectAttributeApi
            .list(accessToken)
            .then((data) => {
              setError(null);
              return data;
            })
            .catch((error) => {
              setError(error);
              console.log(error);
            })
            .finally(() =>
              setDataLoaded((prevData) => {
                return {
                  ...prevData,
                  attributesLoaded: true,
                };
              })
            );

          const groupDataByName = Object.groupBy(data, ({ name }) => name);
          setProjectAttributes(groupDataByName);
        })();
      })
      .catch((error) => {
        setError(error);
      });
  }, [instance, accounts, props.configuration.enableProjectAttributes]);

  useEffect(() => {
    if (!isTrue(props.configuration.enableProjectWorkflowStatus)) {
      return;
    }

    if (!instance || !accounts || accounts.length === 0) {
      return;
    }

    const accessTokenRequest = {
      ...tokenRequest,
      account: accounts[0],
    };

    instance
      .acquireTokenSilent(accessTokenRequest)
      .then((accessTokenResponse) => {
        const accessToken = accessTokenResponse.accessToken;
        (async () => {
          const data = await workflowStatusApi
            .get(accessToken, "Project")
            .then((data) => {
              setError(false);
              return data;
            })
            .catch((e) => {
              setError(true);
              console.log(e);
            })
            .finally(() =>
              setDataLoaded((prevData) => {
                return {
                  ...prevData,
                  workflowStatusesLoaded: true,
                };
              })
            );

          setWorkflowStatuses(data);
        })();
      })
      .catch((error) => {
        setError(true);
      });
  }, [instance, accounts, props.configuration.enableProjectWorkflowStatus]);

  useEffect(() => {
    if (
      dataLoaded.categoriesLoaded &&
      dataLoaded.currencyLoaded &&
      (!isTrue(props.configuration.enableProjectAttributes) ||
        (isTrue(props.configuration.enableProjectAttributes) &&
          dataLoaded.attributesLoaded)) &&
      (!isTrue(props.configuration.enableProjectWorkflowStatus) ||
        (isTrue(props.configuration.enableProjectWorkflowStatus) &&
          dataLoaded.workflowStatusesLoaded))
    ) {
      reset({
        ...attributeDefaultValuesObject,
        id: project.id,
        name: project.name,
        projectCategoryId: project.projectCategoryId,
        uoM: project.uoM,
        currency: project.currency,
        validFrom: fromDate,
        validTo: toDate,
        description: project.description,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoaded]);

  const handleUomDropdownSelection = (event) => {
    const result =
      event.target.value === unitOfMeasurements.COST ||
      event.target.value === unitOfMeasurements.PRICE;

    if (!result) {
      reset({
        ...getValues(),
        currency: null,
      });
    }
    setShowCurrency(result);
  };

  const onProjectBasicDetailsEdit = async (data) => {
    if (isTrue(props.configuration.enableProjectAttributes)) {
      const projectAttributeDetails = Object.entries(projectAttributes)
        .map(([key, values]) => {
          const attributeDetail = project.projectAttributeDetails.find(
            (d) => d.attribute.name === key
          );
          const attributeId = data[key];
          if (attributeId !== "") {
            return {
              attribute: values.find((v) => v.id === parseInt(attributeId)),
              attributeId: attributeId,
              id: attributeDetail ? attributeDetail.id : 0,
              projectId: project.id,
            };
          } else return null;
        })
        .filter((f) => f != null);

      data = {
        ...data,
        projectAttributeDetails: projectAttributeDetails,
      };
    } else {
      project.projectAttributeDetails = null;
    }

    if (project.isFileUpload) {
      const dateRange = getYearAndMonthRange(data.validFrom, data.validTo);
      if (dateRange && dateRange.length > 0 && dateRange.length <= 12) {
        props.handleFileUpload(data, file, setFileUploadingStatus);
        setTimeHorizonValuesError(false);
      } else {
        setTimeHorizonValuesError(true);
      }
    } else {
      setDatesTouched(false);
      setProject({
        ...project,
        ...data,
        validFrom: new Date(data.validFrom),
        validTo: new Date(data.validTo),
      });
      setDates({
        validFrom: data.validFrom,
        validTo: data.validTo,
        refreshTimeHorizonTable: true,
      });
    }
  };

  const handleValidFromChange = (date) => {
    if (!date) {
      return;
    }

    setValidFromDate(date);

    if (date > validToDate) {
      setValidToDate(date);
    }

    handleDatesKeyPress(date);
  };

  const handleValidToChange = (date) => {
    if (!date) {
      return;
    }

    setValidToDate(date);
    handleDatesKeyPress(date);
  };

  const handleDatesKeyPress = (e) => {
    setDatesTouched(true);
    setDates({ ...dates, refreshTimeHorizonTable: false });
  };

  const onTimeHorizonEdit = async (data) => {
    const updatedTimeHorizonValues = timeHorizonValues.map(
      (timeHorizonValue) => {
        return Object.assign(
          {},
          ...props.configuration.attributeConfigurations.map((a) => {
            return {
              ...timeHorizonValue,
              projectId: project.id,
              [a.attributeName]:
                data[
                  `${a.attributeName}-${timeHorizonValue.formattedTimeBucket}`
                ],
            };
          })
        );
      }
    );

    const request = {
      ...project,
      projectTimeHorizon: updatedTimeHorizonValues,
      validFrom: project.validFrom,
      validTo: project.validTo,
    };

    props.handleEditItemArea(request);
  };

  useEffect(() => {
    if (dates && dates.refreshTimeHorizonTable) {
      const dateRange = getYearAndMonthRange(dates.validFrom, dates.validTo);

      if (dateRange && dateRange.length > 0 && dateRange.length <= 12) {
        const dateRangeWithIds = dateRange.map((range) => {
          const isInThePast = isDateInThePast(range.timeBucket);
          const existingTimeHorizonValue = originalTimeHorizonValues.find(
            (m) => m.formattedTimeBucket === range.formattedTimeBucket
          );
          if (existingTimeHorizonValue) {
            const newRange = {
              ...existingTimeHorizonValue,
              isDateInThePast: isInThePast,
            };
            return newRange;
          } else {
            return {
              ...range,
              isDateInThePast: isInThePast,
              value: !range.value && isInThePast ? 0 : range.value,
            };
          }
        });
        setTimeHorizonValuesError(false);
        setTimeHorizonValues(dateRangeWithIds);
      } else {
        setTimeHorizonValuesError(true);
        setTimeHorizonValues([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dates]);

  const getTimeHorizonTemplate = () => {
    const timeHorizonLabels = timeHorizonValues.map((timeHorizon) => {
      return (
        <th
          scope="col"
          className="text-end"
          key={`${project.id}-${timeHorizon.label}`}
        >
          <div style={{ width: "8rem" }}>{timeHorizon.label}</div>
        </th>
      );
    });

    const timeHorizon = props.configuration.attributeConfigurations.map((c) => {
      return (
        <tr key={c.attributeName}>
          {c.displayName ? (
            <th scope="col">
              <div>
                <OverFlowToolTip>{c.displayName}</OverFlowToolTip>
              </div>
            </th>
          ) : null}

          {timeHorizonValues.map((timeHorizon) => {
            return (
              <td
                className="text-end"
                key={`${project.id}-${c.attributeName}-${timeHorizon.formattedTimeBucket}`}
              >
                <div>
                  <input
                    className={`text-end form-control ${
                      timeHorizonErrors?.[
                        `${c.attributeName}-${timeHorizon.formattedTimeBucket}`
                      ]
                        ? "is-invalid"
                        : ""
                    } ${timeHorizon.isDateInThePast ? "disabled" : ""}`}
                    name={`${c.attributeName}-${timeHorizon.formattedTimeBucket}`}
                    type="number"
                    readOnly={!props.hasWritePermissions(c.attributeName)}
                    {...timeHorizonRegister(
                      `${c.attributeName}-${timeHorizon.formattedTimeBucket}`,
                      {
                        required: {
                          value: true,
                          message: `The ${timeHorizon.label.toLowerCase()} field is required.`,
                        },
                      }
                    )}
                    step="any"
                    defaultValue={
                      timeHorizon[c.attributeName.toLowerCase()]
                        ? timeHorizon[c.attributeName.toLowerCase()]
                        : 0
                    }
                  />
                  {timeHorizonErrors?.[
                    `${c.attributeName}-${timeHorizon.formattedTimeBucket}`
                  ] && (
                    <span className="invalid-feedback">
                      {
                        timeHorizonErrors?.[
                          `${c.attributeName}-${timeHorizon.formattedTimeBucket}`
                        ]?.message
                      }
                    </span>
                  )}
                </div>
              </td>
            );
          })}
        </tr>
      );
    });

    return (
      <div
        className="col-12 animate fadeIn slow"
        key="edit-project-time-horizon-container"
      >
        <form
          onSubmit={timeHorizonHandleSubmit(onTimeHorizonEdit)}
          key="edit-project-time-horizon-parameters"
          encType="multipart/form-data"
        >
          <div className="row">
            <div className="table-responsive scrollbar horizontal">
              <div className="scrollbar-content">
                <div className={`col-${timeHorizonValues.length}`}>
                  <table
                    className="time-horizon table table-lg table-borderless table-header-border"
                    key="edit-project-time-horizon-table"
                  >
                    <thead>
                      <tr>
                        <th scope="col">
                          <div className="text-start">
                            {props.configuration.attributeDisplayNameHeader !=
                            null
                              ? props.configuration.attributeDisplayNameHeader
                              : "Name"}
                          </div>
                        </th>
                        {timeHorizonLabels}
                      </tr>
                    </thead>
                    <tbody>{timeHorizon}</tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div className="my-3">
            <button
              type="submit"
              className="button me-2"
              disabled={!props.hasWritePermissions()}
            >
              Select Item Areas
            </button>
            <button
              type="button"
              className="button danger"
              onClick={() => props.handleCancel()}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  };

  const projectName = watch("name");
  useEffect(() => {
    props.validateProjectName(projectName, project.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectName]);

  if (
    !dataLoaded.categoriesLoaded ||
    !dataLoaded.currencyLoaded ||
    (isTrue(props.configuration.enableProjectAttributes) &&
      !dataLoaded.attributesLoaded) ||
    (isTrue(props.configuration.enableProjectWorkflowStatus) &&
      !dataLoaded.workflowStatusesLoaded)
  ) {
    return <Spinner />;
  }

  if (error) {
    return <Error error={error} />;
  }

  return (
    <React.Fragment>
      {dataLoaded.categoriesLoaded &&
      dataLoaded.currencyLoaded &&
      projectCategories &&
      projectCategories.length > 0 ? (
        <div className="row animate fadeIn slow" key="edit-project-container">
          <div className="sticky-header stuck">
            <div className="basic-details">
              <div className="flex name">{watch("name")} </div>
              <div className="info">
                {watch("workflowStatus")} {watch("uoM")} {watch("currency")}
              </div>
            </div>
          </div>
          {!props.hasWritePermissions() ? (
            <div className="mt-2 text-error">
              You don't have time horizon row level permissions
            </div>
          ) : null}
          <div className="flex pt-2">
            <div className="flex">
              <button
                type="button"
                className="link ms-n2"
                onClick={() => props.handleCancel()}
              >
                <span className="arrow-icon back">
                  <BackIcon />
                </span>
                <span className="font-weight-medium">Back</span>
              </button>
            </div>
          </div>
          <div className="col-12 pt-3">
            <h4>Edit Project Details</h4>
          </div>

          <div className="col-6" key="edit-project-parameters-container">
            <form
              onSubmit={handleSubmit(onProjectBasicDetailsEdit)}
              key="edit-project-parameters"
              encType="multipart/form-data"
            >
              <div className="pb-3">
                <h5>Basic Details</h5>
              </div>
              <div className="form-group">
                <label className="form-label">Name</label>
                <input
                  className={`form-control ${errors.name ? "is-invalid" : ""}`}
                  name="name"
                  {...register("name", {
                    required: {
                      value: true,
                      message: "The name field is required.",
                    },
                  })}
                />
                {errors.name && (
                  <span className="invalid-feedback">
                    {errors.name.message}
                  </span>
                )}
              </div>

              {dataLoaded.workflowStatusesLoaded && workflowStatuses.length ? (
                <React.Fragment>
                  <div className="form-group">
                    <label className="form-label">Workflow Status</label>
                    <select
                      name="workflowStatus"
                      className={`form-control ${
                        errors.workflowStatus ? "is-invalid" : ""
                      }`}
                      change=""
                      {...register("workflowStatus", {
                        required: {
                          value: true,
                          message: "The workflow status field is required.",
                        },
                      })}
                    >
                      {workflowStatuses.map((option) => {
                        return (
                          <option value={option.value} key={option.value}>
                            {option.description}
                          </option>
                        );
                      })}
                    </select>
                    {errors.workflowStatus && (
                      <span className="invalid-feedback">
                        {errors.workflowStatus.message}
                      </span>
                    )}
                  </div>
                </React.Fragment>
              ) : null}
              <div className="form-group">
                <label className="form-label">Category</label>
                <select
                  name="projectCategoryId"
                  className={`form-control ${
                    errors.projectCategoryId ? "is-invalid" : ""
                  }`}
                  {...register("projectCategoryId", {
                    required: {
                      value: true,
                      message: "The category field is required.",
                    },
                  })}
                >
                  <option value="" key="Select Category">
                    Select Category
                  </option>
                  {projectCategories.map((option) => {
                    return (
                      <option value={option.id} key={option.name}>
                        {option.name}
                      </option>
                    );
                  })}
                </select>
                {errors.projectCategoryId && (
                  <span className="invalid-feedback">
                    {errors.projectCategoryId.message}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">UoM</label>
                <select
                  name="uoM"
                  className={`form-control ${errors.uoM ? "is-invalid" : ""}`}
                  change=""
                  {...register("uoM", {
                    onChange: (e) => handleUomDropdownSelection(e),
                    required: {
                      value: true,
                      message: "The uom field is required.",
                    },
                  })}
                >
                  <option value="">Select UoM</option>
                  {uomData.map((option) => {
                    return (
                      <option value={option.value} key={option.value}>
                        {option.description}
                      </option>
                    );
                  })}
                </select>
                {errors.uoM && (
                  <span className="invalid-feedback">{errors.uoM.message}</span>
                )}
              </div>

              {showCurrency ? (
                <div className="form-group">
                  <label className="form-label">Currency</label>
                  <select
                    defaultValue={project.currency}
                    disabled={currencyData.length > 0 ? false : true}
                    name="currency"
                    className={`form-control ${
                      errors.currency ? "is-invalid" : ""
                    }`}
                    {...register("currency", {
                      required: {
                        value: true,
                        message: "The currency field is required.",
                      },
                    })}
                  >
                    <option value="">Select Currency</option>
                    {currencyData.map((option) => {
                      return (
                        <option
                          value={option.description}
                          key={option.description}
                        >
                          {option.description}
                        </option>
                      );
                    })}
                  </select>
                  {errors.currency && (
                    <span className="invalid-feedback">
                      {errors.currency.message}
                    </span>
                  )}
                </div>
              ) : (
                ""
              )}

              <div className="form-group pb-3">
                <label className="form-label">Description</label>
                <textarea
                  rows="3"
                  className={`form-control ${
                    errors.description ? "is-invalid" : ""
                  }`}
                  name="description"
                  {...register("description", {
                    required: {
                      value: true,
                      message: "The description field is required.",
                    },
                  })}
                ></textarea>
                {errors.description && (
                  <span className="invalid-feedback">
                    {errors.description.message}
                  </span>
                )}
              </div>

              {dataLoaded.attributesLoaded && projectAttributes ? (
                <React.Fragment>
                  <div>
                    <h5 className="py-3 border-top">Attributes</h5>
                  </div>
                  <div className="row pb-3">
                    {Object.entries(projectAttributes).map(([key, values]) => {
                      return (
                        <div className="col-6" key={`${key}container`}>
                          <div className="form-group">
                            <label className="form-label">
                              {key} {values[0].isMandatory ? "" : "(Optional)"}
                            </label>
                            <select
                              name={key}
                              className={`form-control ${
                                errors?.[key] ? "is-invalid" : ""
                              }`}
                              change=""
                              {...register(`${key}`, {
                                required: {
                                  value: values[0].isMandatory ? true : false,
                                  message: `The ${key.toLowerCase()} field is required.`,
                                },
                              })}
                            >
                              <option value="" key={`${key}selectAttribute`}>
                                Select Attribute
                              </option>
                              {values.map((option) => {
                                return (
                                  <option value={option.id} key={option.id}>
                                    {option.value}
                                  </option>
                                );
                              })}
                            </select>
                            {errors?.[key] && (
                              <span className="invalid-feedback">
                                {errors?.[key].message}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </React.Fragment>
              ) : null}

              <div className="row">
                <div>
                  <h5 className="py-3 border-top">Time Horizon</h5>
                </div>
                <div className="form-group col-md-6">
                  <label className="form-label">
                    Valid From ({dateFormat.toLowerCase()})
                  </label>
                  <Controller
                    control={control}
                    name="validFrom"
                    rules={{
                      required: true,
                    }}
                    render={({
                      field: { onChange, onBlur, name, value },
                      formState: { errors },
                    }) => (
                      <>
                        <DatePicker
                          portalId="root"
                          showPreviousMonths={false}
                          className={`form-control ${
                            errors.validFrom ? "is-invalid" : ""
                          }`}
                          minDate={todayDate}
                          maxDate={validToMaxDate}
                          selected={validFromDate ?? ""}
                          onChange={(date) => {
                            onChange(date);
                            handleValidFromChange(date);
                          }}
                          placeholderText={dateFormat.toLowerCase()}
                          dateFormat={dateFormat}
                          showMonthYearPicker
                        />
                        {errors && errors.validFrom && (
                          <span className="invalid-feedback">
                            The valid from field is required
                          </span>
                        )}
                      </>
                    )}
                  />
                </div>
                <div className="form-group col-md-6">
                  <label className="form-label">
                    Valid To ({dateFormat.toLowerCase()})
                  </label>
                  <Controller
                    control={control}
                    name="validTo"
                    rules={{
                      required: true,
                    }}
                    render={({
                      field: { onChange, onBlur, name, value },
                      formState: { errors },
                    }) => (
                      <>
                        <DatePicker
                          portalId="root"
                          showPreviousMonths={false}
                          className={`form-control ${
                            errors.validTo ? "is-invalid" : ""
                          }`}
                          minDate={validFromDate}
                          maxDate={validToMaxDate}
                          selected={validToDate ?? ""}
                          onChange={(date) => {
                            onChange(date);
                            handleValidToChange(date);
                          }}
                          placeholderText={dateFormat.toLowerCase()}
                          dateFormat={dateFormat}
                          showMonthYearPicker
                        />
                        {errors && errors.validTo && (
                          <span className="invalid-feedback">
                            The valid to field is required
                          </span>
                        )}
                      </>
                    )}
                  />
                </div>
              </div>
              {!project.isFileUpload ? (
                <div className="mb-3 mt-1">
                  <button type="submit" className="button me-2">
                    Time Horizon
                  </button>
                  {dates.refreshTimeHorizonTable &&
                  !showTimeHorizonValuesError ? (
                    ""
                  ) : (
                    <button
                      type="button"
                      className="button danger"
                      onClick={() => props.handleCancel()}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              ) : (
                <div>
                  <div className="form-group">
                    <label className="form-label">
                      CSV File Value Column Name
                    </label>
                    <select
                      name="timeHorizonColumnName"
                      className={`form-control ${
                        errors.fileUploadValueColumnName ? "is-invalid" : ""
                      }`}
                      {...register("timeHorizonColumnName", {
                        required: {
                          value: true,
                          message:
                            "The CSV file value column name field is required.",
                        },
                      })}
                    >
                      <option value="">Select Name</option>
                      {props.fileUploadColumnValueNames.map((option) => {
                        return (
                          <option
                            value={option.attributeName}
                            key={option.attributeName}
                          >
                            {option.displayName}
                          </option>
                        );
                      })}
                    </select>
                    {errors.timeHorizonColumnName && (
                      <span className="invalid-feedback">
                        {errors.timeHorizonColumnName.message}
                      </span>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">CSV File</label>
                    <input
                      {...rest}
                      type="file"
                      className={`form-control ${
                        errors.file ? "is-invalid" : ""
                      }`}
                      name="file"
                      {...register("file", {
                        required: {
                          value: true,
                          message: "The file field is required.",
                        },
                      })}
                      onChange={saveFile}
                      ref={(e) => {
                        ref(e);
                        fileInputRef.current = e;
                      }}
                    />
                    {errors.file && (
                      <span className="invalid-feedback">
                        {errors.file.message}
                      </span>
                    )}
                  </div>
                  <div className="mt-4 form-group">
                    <button
                      type="submit"
                      className="button primary me-2"
                      disabled={fileUploadingStatus.isUploading}
                    >
                      Upload
                    </button>
                    <button
                      type="button"
                      className={`button ${
                        fileUploadingStatus.response?.status?.toLowerCase() ===
                        "success"
                          ? ""
                          : "danger"
                      }`}
                      onClick={() => props.handleCancel()}
                    >
                      {fileUploadingStatus.response?.status?.toLowerCase() ===
                      "success"
                        ? "Back"
                        : "Cancel"}
                    </button>
                  </div>
                  {fileUploadingStatus.response ? (
                    <React.Fragment>
                      <div
                        className={`flex text-${fileUploadingStatus.response.status.toLowerCase()}`}
                      >
                        {fileUploadingStatus.response.message}
                      </div>

                      {fileUploadingStatus.response.records
                        ? props.getFileUploadErrorRecordsTemplate(
                            fileUploadingStatus.response.records
                          )
                        : null}
                    </React.Fragment>
                  ) : fileUploadingStatus.isUploading ? (
                    <span className="ms-2">
                      <span className="me-2">Uploading</span>
                      <span
                        className="spinner-grow spinner-grow-1 me-1"
                        role="status"
                      ></span>
                      <span
                        className="spinner-grow  spinner-grow-2 me-1"
                        role="status"
                      ></span>
                      <span className="spinner-grow me-1" role="status"></span>
                    </span>
                  ) : null}
                </div>
              )}
              <div className="pb-4"></div>
            </form>
          </div>

          {!datesTouched &&
          dates.refreshTimeHorizonTable &&
          timeHorizonValues.length > 0 ? (
            <React.Fragment>{getTimeHorizonTemplate()}</React.Fragment>
          ) : (
            ""
          )}
          {showTimeHorizonValuesError ? (
            <div className="col-12 mb-3 text-danger">
              Please check valid from and valid to dates. Time horizon should be
              rolling year.
            </div>
          ) : (
            ""
          )}
        </div>
      ) : (
        <div className="row text-danger m-3">
          Please add project categories before adding projects.
          <div className="col">
            <NavLink to={`/projects/categories`} className="button extra-small">
              Click to add
            </NavLink>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default EditProject;
